/*
 * <BeIconView> -- Tracker's "Icon View" and "Mini Icon View". The same items
 * BeTable draws as rows, drawn as icons on a snap grid; the mode picks the icon
 * size and the track, and nothing else. See components/icon-view.scss.
 */

import type { KeyboardEvent, ReactNode } from "react";
import { type BeProps, beComponent } from "./create.tsx";
import { type BeItem, beIconSrc } from "./item.ts";
import { useBeListKeyboard } from "./list-keyboard.ts";
import { pixelText } from "./pixel-text.tsx";
import { BeRow, BeStack } from "./Stack.tsx";

/** An item with the label the icon views draw under it; BeTable reads its from `cells`. */
export type BeIconViewItem = BeItem & { name: ReactNode };

export type BeIconViewMode = "icon" | "mini";

export type BeIconViewProps = BeProps<
	"div",
	{
		items: BeIconViewItem[];
		/** "icon" is B_LARGE_ICON on a 60x60 track, "mini" B_MINI_ICON on 96x20. */
		mode?: BeIconViewMode;
		/** Ids of selected items (controlled). */
		selectedIds?: string[];
		/** Called when an item is clicked; makes items interactive. */
		onSelectItem?: (id: string, item: BeIconViewItem) => void;
		/** Called when an item is double-clicked. */
		onDoubleClickItem?: (id: string, item: BeIconViewItem) => void;
	}
>;

const labelText = (item: BeIconViewItem): string | null => (typeof item.name === "string" ? item.name : null);

export const BeIconView = beComponent<BeIconViewProps, HTMLDivElement>("BeIconView", (props, be) => {
	const { items, mode = "icon", selectedIds, onSelectItem, onDoubleClickItem, scale, className, style, ...rest } = props;

	const mini = mode === "mini";
	const selected = new Set(selectedIds ?? []);
	const selectable = onSelectItem !== undefined;
	const Pose = mini ? BeRow : BeStack;

	const root = be("be-icon-view", mini && "be-icon-view--mini");
	const nav = useBeListKeyboard(items, selected, { layout: "grid", textOf: labelText, onMove: onSelectItem, ref: root.ref });

	function handleItemKeyDown(e: KeyboardEvent<HTMLElement>, item: BeIconViewItem) {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			(onDoubleClickItem ?? onSelectItem)?.(item.id, item);
			return;
		}
		nav.handleKey(e, item.id);
	}

	return (
		<div {...rest} {...root} ref={nav.setContainer} role={selectable ? "listbox" : undefined}>
			{items.map((item) => {
				const src = beIconSrc(item.icon, mini ? "mini" : "large");
				return (
					<Pose
						key={item.id}
						data-key={item.id}
						className="be-icon-view__item"
						align="center"
						gap={mini ? "tight" : "none"}
						role={selectable ? "option" : undefined}
						aria-selected={selectable && selectedIds ? selected.has(item.id) : undefined}
						tabIndex={selectable ? (item.id === nav.rovingId ? 0 : -1) : undefined}
						onClick={onSelectItem ? () => onSelectItem(item.id, item) : undefined}
						onDoubleClick={onDoubleClickItem ? () => onDoubleClickItem(item.id, item) : undefined}
						onFocus={selectable ? () => nav.setActiveId(item.id) : undefined}
						onKeyDown={selectable ? (e: KeyboardEvent<HTMLElement>) => handleItemKeyDown(e, item) : undefined}
					>
						{src ? <img className="be-icon-view__icon" src={src} alt="" /> : null}
						<span className="be-icon-view__label">{pixelText(item.name)}</span>
					</Pose>
				);
			})}
		</div>
	);
});
